const STORAGE_KEY = "qogitaWatchlist.prefs.v1";

const DEFAULT_PREFS = {
  pageSize: 50,
  search: "",
  qogitaStatus: "all",
  syncMaxAsins: "",
  keepaQuerySelection: "",
  qogitaSchedule: "0 */6 * * *",
  keepaSchedule: "0 3 * * *",
  discordWebhook: "",
  targetQuantity: 10,
};

/** Short copy for tiered Keepa Nettospanne (matches backend pricing.py). */
export const KEEPA_NETTOPANNE_MARGIN_SUMMARY =
  "13% Nettospanne when avg VK (30d) is under 25 EUR, otherwise 10%.";

export function formatTargetEkMarginCaption(item) {
  if (item?.margin_percent == null || item.margin_percent === "") return null;
  const pct = Number(item.margin_percent);
  if (Number.isNaN(pct)) return null;
  return `${pct}% Nettospanne`;
}

export function loadWatchlistPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveWatchlistPrefs(partial) {
  const next = { ...loadWatchlistPrefs(), ...partial };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function resolveCronValue(value, fallback) {
  if (typeof value === "string") return value;
  if (value?.target?.value) return value.target.value;
  return fallback;
}

export function formatKeepaQuerySelection(obj) {
  return JSON.stringify(obj, null, 2);
}

/** Parse pasted Keepa finder JSON; returns null when empty. */
export function parseKeepaQuerySelection(text) {
  if (!text || !String(text).trim()) return null;
  const parsed = JSON.parse(text);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Keepa query must be a JSON object");
  }
  return parsed;
}

export const QOGITA_STATUS_FILTERS = [
  { value: "all", label: "All items" },
  { value: "archived", label: "Archived (ignored)" },
  { value: "targets_met", label: "Targets met" },
  { value: "in_catalog", label: "In Qogita catalog" },
  { value: "not_in_catalog", label: "Not in Qogita catalog" },
  { value: "keepa_only", label: "Keepa pipeline only" },
  { value: "keepa_skipped", label: "Keepa skipped (no EAN / no product)" },
  { value: "keepa_skipped_no_ean", label: "Keepa skipped — no EAN" },
  {
    value: "keepa_skipped_no_product",
    label: "Keepa skipped — no product data",
  },
  {
    value: "keepa_skipped_analyze",
    label: "Keepa skipped — no EK (missing metrics)",
  },
];

/** Estimate cron fires in a rolling 24h window (coarse; for UI budget check). */
export function estimateCronFiresPerDay(expression) {
  if (!expression || typeof expression !== "string") return null;
  const parts = expression.trim().split(/\s+/);
  // Support optional leading seconds field (6-part cron)
  const cronParts =
    parts.length >= 6 ? parts.slice(1, 6) : parts.length >= 5 ? parts.slice(0, 5) : null;
  if (!cronParts) return null;
  const [minute, hour, dayOfMonth, , dayOfWeek] = cronParts;

  const expandField = (field, min, max) => {
    if (field === "*") return max - min + 1;
    // */N or N/N (e.g. */2, 0/2)
    const stepMatch = field.match(/^(?:\*|(\d+))\/(\d+)$/);
    if (stepMatch) {
      const start = stepMatch[1] != null ? Number(stepMatch[1]) : min;
      const step = Number(stepMatch[2]);
      if (!step || step < 1 || Number.isNaN(start)) return null;
      let count = 0;
      for (let i = start; i <= max; i += step) count += 1;
      return count;
    }
    if (field.includes(",")) return field.split(",").filter(Boolean).length;
    if (field.includes("-")) {
      const rangeStep = field.match(/^(\d+)-(\d+)(?:\/(\d+))?$/);
      if (!rangeStep) return null;
      const a = Number(rangeStep[1]);
      const b = Number(rangeStep[2]);
      const step = rangeStep[3] ? Number(rangeStep[3]) : 1;
      if (Number.isNaN(a) || Number.isNaN(b) || !step || step < 1) return null;
      let count = 0;
      for (let i = Math.min(a, b); i <= Math.max(a, b); i += step) count += 1;
      return count;
    }
    return 1;
  };

  // Day-of-month / day-of-week restrictions → treat as ≤1/day when both constrained
  if (dayOfMonth !== "*" && dayOfWeek !== "*") {
    const hours = expandField(hour, 0, 23);
    const mins = expandField(minute, 0, 59);
    if (hours == null || mins == null) return null;
    return hours * mins;
  }
  if (dayOfMonth !== "*" || dayOfWeek !== "*") {
    const hours = expandField(hour, 0, 23);
    const mins = expandField(minute, 0, 59);
    if (hours == null || mins == null) return null;
    return hours * mins;
  }

  const hours = expandField(hour, 0, 23);
  const mins = expandField(minute, 0, 59);
  if (hours == null || mins == null) return null;
  return hours * mins;
}

export const CATALOG_DOWNLOAD_MAX_PER_24H = 50;

export const KEEPA_FETCH_STATUS_LABELS = {
  skipped_no_ean: "Keepa: no EAN",
  skipped_no_product: "Keepa: no product",
  skipped_analyze: "Keepa: no EK",
};

const ASIN_GTIN_PREFIX = "ASIN:";

/** Resolve Amazon.de ASIN from a watchlist row. */
export function resolveWatchlistAsin(item) {
  if (!item) return null;
  if (item.asin) return item.asin.trim();
  if (item.gtin?.startsWith(ASIN_GTIN_PREFIX)) {
    return item.gtin.slice(ASIN_GTIN_PREFIX.length).trim();
  }
  return null;
}

export function amazonDeProductUrl(asin) {
  if (!asin) return null;
  return `https://www.amazon.de/dp/${encodeURIComponent(asin)}`;
}

export function watchlistAmazonUrl(item) {
  return amazonDeProductUrl(resolveWatchlistAsin(item));
}

/** Qogita catalog search for a GTIN/EAN. */
export function qogitaCatalogSearchUrl(gtin) {
  if (!gtin || gtin.startsWith(ASIN_GTIN_PREFIX)) return null;
  const value = String(gtin).trim();
  if (!value) return null;
  return `https://www.qogita.com/categories/?query=${encodeURIComponent(
    value,
  )}`;
}

/** Human-readable GTIN cell (hide ASIN: stub prefix when ASIN column exists). */
export function formatWatchlistGtin(item) {
  if (!item?.gtin) return "—";
  if (item.gtin.startsWith(ASIN_GTIN_PREFIX) && item.asin) {
    return item.asin;
  }
  return item.gtin;
}
